import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '../../../../shared';
import { Observable, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../app.reducers';
import { SpUtilities } from '../../../../shared/services/utilities';
import { PropertyService } from '../../shared/services/property.service';
import { getSelectedProperty, getSelectedFiscalYear, getSelectedYearPropertyFinancial } from '../../property-store/property.selectors';
import { Router } from '@angular/router';
import { CommunicationStateComponent } from '../../../../shared/components/communication-state';
import { Property, PropertyFinancial } from '../../shared/models';
import { CollectionFrequency } from 'app/shared/models';
import { LoadPropertyFinancialAction } from '../../property-store/property.actions';
import { ClearFinancialHealthReportFromStateAction, ClearRealTimeFinancialHealthReportFromStateAction } from 'app/internal/reports/redux/reports.actions';
import { FiscalYear, PlanType, Workspace } from 'app/internal/shared';
import { Plan } from '../../../plans/shared/models';
import { getPlans } from '../../../plans/plans-store/plans.selectors';
import { LoadPlansAction } from '../../../plans/plans-store';
import { PlanRequest } from '../../../plans/shared/models/plan-request';
import { SubSink } from 'subsink';
import { getWorkspaceSettings } from 'app/internal/workspace/workspace-store/workspace.selectors';
import * as ReservesActions from '../../../reserves/store/reserves.actions';

@Component({
  selector: 'sp-property-financials',
  templateUrl: './financials.component.html',
  styleUrls: ['./financials.component.scss'],
  standalone: false
})
export class PropertyFinancialsComponent implements OnInit, OnDestroy {

  plans$: Observable<Plan[]>;
  planType = PlanType;
  property$: Observable<Property>;
  selectedFiscalYear$: Observable<FiscalYear>;
  propertyFinancial$: Observable<PropertyFinancial>;
  workspaceSettings$: Observable<Workspace>;
  property: Property;
  propertyId: string;
  propertyFinancialId: string;
  fiscalYearStartDates: Array<string>;
  hoaFeeCollectionFrequencies: Array<CollectionFrequency>;
  fiscalYear: number;
  isPreviousFiscalYear = false;
  startFiscalYear: Date;
  endFiscalYear: Date;
  originalFiscalStartMonth: number;
  saving = false;
  formGroup: UntypedFormGroup = new UntypedFormGroup({});

  @ViewChild(CommunicationStateComponent, { static: true }) public communicationState: CommunicationStateComponent;

  private subs = new SubSink();
  constructor(
    private router: Router,
    private utils: SpUtilities,
    private propertyService: PropertyService,
    private store: Store<AppState>,
    private fb: UntypedFormBuilder) {

    this.plans$ = this.store.pipe(select(getPlans));
    this.property$ = this.store.pipe(select(getSelectedProperty));
    this.selectedFiscalYear$ = this.store.pipe(select(getSelectedFiscalYear));
    this.propertyFinancial$ = this.store.pipe(select(getSelectedYearPropertyFinancial));
    this.workspaceSettings$ = this.store.pipe(select(getWorkspaceSettings));
  }

  ngOnInit(): void {

    this.fiscalYearStartDates = this.getFiscalStartDates();
    this.hoaFeeCollectionFrequencies = this.utils.getFrequencies();

    this.subs.sink = combineLatest([this.selectedFiscalYear$, this.property$])
      .pipe(filter(([fy, p]) => fy != null && p != null && !fy.requireSetup))
      .subscribe(([fiscalYear, property]) => {
        if (this.propertyId != null && this.propertyId !== property.propertyId) {
          // property changed, no need to load this information because we'll be redirected to the dashboard.
          return;
        }
        // if (!fiscalYear.isCurrentFiscalYear) {
        //   this.router.navigate(['/useraccount/dashboard']);
        //   return;
        // }
        this.property = property;
        this.propertyId = property.propertyId;
        this.fiscalYear = fiscalYear.year;
        this.isPreviousFiscalYear = fiscalYear.isPreviousFiscalYear;

        this.store.dispatch(new LoadPropertyFinancialAction({ propertyId: this.propertyId, fiscalYear: fiscalYear.year }));
        this.store.dispatch(new LoadPlansAction(new PlanRequest(property.propertyId, fiscalYear.year, true)));
      });

    this.subs.sink = this.propertyFinancial$
      .subscribe((propertyFinancial: PropertyFinancial) => {
        if (propertyFinancial == null) {
          return;
        }
        this.propertyFinancialId = propertyFinancial.propertyFinancialId;
        this.originalFiscalStartMonth = propertyFinancial.fiscalYearStartMonth;
        this.initForm(propertyFinancial);
      });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onSave() {

    if (this.communicationState != null) {
      this.communicationState.setStartState();
    }

    const fiscalYearStartMonth = this.formGroup.value.fiscalYearStartMonth;
    const startingReserveBalance = this.formGroup.value.startingReserveBalance;
    const averageHoaAnnualFee = this.formGroup.value.averageHoaAnnualFee;
    const previousHoaAnnualFee = this.formGroup.value.previousHoaAnnualFee;
    const annualReserveContribution = this.formGroup.value.annualReserveContribution;
    const inflationRate = this.formGroup.value.inflationRate;
    const interestRate = this.formGroup.value.interestRate;
    const reserveContributionIncreaseRate = this.formGroup.value.reserveContributionIncreaseRate;
    const feeCollectionFrequencyId = this.formGroup.value.feeCollectionFrequencyId;
    const updateAllPlans = this.formGroup.value.updateAllPlans;

    const propertyFinancial = <PropertyFinancial>{};
    propertyFinancial.propertyFinancialId = this.propertyFinancialId;
    propertyFinancial.propertyId = this.propertyId;
    propertyFinancial.fiscalYearStartMonth = fiscalYearStartMonth;
    propertyFinancial.yearEndReserveBalance = startingReserveBalance;
    propertyFinancial.averageHoaAnnualFee = averageHoaAnnualFee;
    propertyFinancial.previousHoaAnnualFee = previousHoaAnnualFee;
    propertyFinancial.annualReserveContribution = annualReserveContribution;
    propertyFinancial.inflationRate = inflationRate;
    propertyFinancial.interestRate = interestRate;
    propertyFinancial.reserveContributionIncreaseRate = reserveContributionIncreaseRate;
    propertyFinancial.feeCollectionFrequencyId = feeCollectionFrequencyId;
    propertyFinancial.updateAllPlans = updateAllPlans;

    const isMonthChanged = this.originalFiscalStartMonth !== fiscalYearStartMonth;

    this.saving = true;
    this.subs.sink = this.propertyService.savePropertyFinancialEntries(propertyFinancial, this.fiscalYear, false)
      .subscribe((res) => {

        this.store.dispatch(ReservesActions.clearReservesData());
        this.store.dispatch(new ClearRealTimeFinancialHealthReportFromStateAction({}));
        this.store.dispatch(new ClearFinancialHealthReportFromStateAction({}));

        if (res.status) {
          // Since the update is not occurring via NgRx, manually update the model via an action
          // this.store.dispatch(new LoadSelectedPropertyAction(new LoadPropertyRequest(this.propertyId)));
          this.store.dispatch(new LoadPropertyFinancialAction({ propertyId: this.propertyId, fiscalYear: this.fiscalYear }));
          this.communicationState.setSuccessState();
          this.saving = false;
          this.setFiscalYearRange(propertyFinancial);

          // if the month changed, reload the page so the fiscal year options are correct.
          if (isMonthChanged) {
            window.location.reload();
          }
        }
      }, (error) => {
        this.communicationState.setFailedState(error);
        this.saving = false;
      });
    this.saving = false;
  }

  //#region private
  private initForm(financial: PropertyFinancial) {

    this.setFiscalYearRange(financial);

    const fiscalYearStartMonth = !financial.fiscalYearStartMonth ? 1 : financial.fiscalYearStartMonth;
    const feeCollectionFrequencyId = !financial.feeCollectionFrequencyId
      ? 1
      : financial.feeCollectionFrequencyId;

    this.formGroup = this.fb.group({
      fiscalYearStartMonth: this.fb.control(fiscalYearStartMonth),
      startingReserveBalance: this.fb.control(financial.yearEndReserveBalance),
      averageHoaAnnualFee: this.fb.control(financial.averageHoaAnnualFee),
      previousHoaAnnualFee: this.fb.control(financial.previousHoaAnnualFee, [Validators.required, CustomValidators.rangeValidator(0, 50000000, null, true)]),
      annualReserveContribution: this.fb.control(financial.annualReserveContribution),
      inflationRate: this.fb.control(financial.inflationRate),
      interestRate: this.fb.control(financial.interestRate),
      reserveContributionIncreaseRate: this.fb.control(financial.reserveContributionIncreaseRate),
      feeCollectionFrequencyId: this.fb.control(feeCollectionFrequencyId),
      updateAllPlans: this.fb.control(false, [Validators.required]),
    });

    // the page is read-only for previous fiscal years.
    if (this.isPreviousFiscalYear) {
      this.formGroup.disable();
    }

    // Validation
    this.configureStartingReserveBalance();
    this.configureAverageHoaAnnualFee();
    this.configureAnnualReserveContributionValidation();
    this.configureInflationRateValidation();
    this.configureInterestRateValidation();
    this.configureReserveContributionIncreaseRateValidation();
  }

  private setFiscalYearRange(propertyFinancial: PropertyFinancial) {
    const zeroBasedMonth = propertyFinancial.fiscalYearStartMonth - 1;
    const fiscalYearStartDate = new Date(this.fiscalYear, zeroBasedMonth, 1);
    const fiscalYearEndDatePlusDay = new Date(this.fiscalYear + 1, propertyFinancial.fiscalYearStartMonth - 1, 1);
    const fiscalYearEndDate = this.addDays(fiscalYearEndDatePlusDay, -1);

    this.startFiscalYear = fiscalYearStartDate;
    this.endFiscalYear = fiscalYearEndDate;
  }

  private fieldHasNoValidValue(field) {
    return field == null ||
      field.valid === false ||
      field.value == null ||
      field.value.toString() === '';
  }

  private getNumberValue(value: string, toInt: boolean, defaultValue = null) {
    let num = null;
    if (toInt) {
      num = parseInt(value, 10);
      return !isNaN(num) && num != null && num !== undefined ? num : defaultValue;
    } else {
      num = parseFloat(value);
      return !isNaN(num) && num != null && num !== undefined ? num : defaultValue;
    }
  }

  private computeReserveContributionLimit() {
    const { averageHoaAnnualFee } = this.formGroup.controls;

    if (this.fieldHasNoValidValue(averageHoaAnnualFee)) {
      return null;
    } else {
      return this.getNumberValue(averageHoaAnnualFee.value, false);
    }
  }

  private configureStartingReserveBalance() {

    const { startingReserveBalance } = this.formGroup.controls;

    startingReserveBalance.clearValidators();

    startingReserveBalance.setValidators(Validators.compose([
      Validators.required,
      CustomValidators.rangeValidator(-50000000, 99000000, null, true)
    ]));
  }

  private configureAverageHoaAnnualFee() {

    const { averageHoaAnnualFee, annualReserveContribution } = this.formGroup.controls;
    averageHoaAnnualFee.clearValidators();

    averageHoaAnnualFee.setValidators(Validators.compose([
      Validators.required,
      CustomValidators.rangeValidator(0, 99000000, null, true)
    ]));

    averageHoaAnnualFee.valueChanges.subscribe(() => {
      this.configureAnnualReserveContributionValidation();
      annualReserveContribution.updateValueAndValidity();
    });
  }

  private configureAnnualReserveContributionValidation() {

    const { annualReserveContribution } = this.formGroup.controls;
    let reserveContributionLimit = this.computeReserveContributionLimit();

    annualReserveContribution.clearValidators();

    if (reserveContributionLimit == null) {
      reserveContributionLimit = 99000000;

      // Add Max Range Validator
      annualReserveContribution.setValidators(Validators.compose([
        Validators.required,
        CustomValidators.rangeValidator(0, reserveContributionLimit, null, true)
      ]));
    } else {

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let message = (min, max) => 'Annual Reserve Contributions should be $0.';

      if (reserveContributionLimit > 0) {
        message = null;
      }
      // Add Calculated Range Validator
      annualReserveContribution.setValidators(Validators.compose([
        Validators.required,
        CustomValidators.rangeValidator(0, reserveContributionLimit, message, true)
      ]));
    }
  }

  private configureInflationRateValidation() {

    const { inflationRate } = this.formGroup.controls;

    inflationRate.clearValidators();

    inflationRate.setValidators(Validators.compose([
      Validators.required,
      CustomValidators.rangeValidator(0, 99.99, (min, max) => `Inflation Rate should be from ${min}% to ${max}%`)
    ]));
  }

  private configureInterestRateValidation() {

    const { interestRate } = this.formGroup.controls;

    interestRate.clearValidators();

    interestRate.setValidators(Validators.compose([
      Validators.required,
      CustomValidators.rangeValidator(0, 99.99, (min, max) => `Interest Rate should be from ${min}% to ${max}%`)
    ]));
  }

  private configureReserveContributionIncreaseRateValidation() {

    const { reserveContributionIncreaseRate } = this.formGroup.controls;

    reserveContributionIncreaseRate.clearValidators();

    reserveContributionIncreaseRate.setValidators(Validators.compose([
      Validators.required,
      CustomValidators.rangeValidator(0, 99.99, (min, max) => `Reserve Contribution Rate should be from ${min}% to ${max}%`)
    ]));
  }

  private addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private getFiscalStartDates(): Array<string> {
    const months = this.utils.getMonthOfYearNames();
    for (let i = 0; i < months.length; i++) {
      months[i] = months[i] + ' 1';
    }
    return months;
  }
  //#endregion
}
